/**
 * Retrieves an override value for the current day from localStorage based on a specified name.
 *
 * The function searches for keys in localStorage that match the pattern `override_dateRange_<name>-<timestamp>`,
 * where `<name>` is the provided name parameter and `<timestamp>` is a numeric timestamp. It ensures that the
 * timestamp corresponds to the current day and removes any outdated keys from previous days.
 *
 * To set an override in localStorage, use the following format:
 *  `localStorage.setItem(`override_dateRange_${name}-${new Date().getTime()}`, true)`.
 *
 * @param name - The name used to construct the localStorage key prefix. Defaults to an empty string.
 * @returns The value associated with the matching key in localStorage, or `null` if no valid key is found.
 *
 * @remarks
 * - If `window` is undefined (e.g., during server-side rendering) or if the `name` parameter is empty, the function
 *   immediately returns `null`.
 * - Keys with timestamps earlier than the start of the current day are removed from localStorage.
 * - The function only considers keys with timestamps up to the end of the current day.
 */
export function getOverrideForToday(name = '') {
  if (typeof window === 'undefined' || !name) return null;

  const prefix = `override_dateRange_${name}`;

  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const endOfDayTimestamp = endOfDay.getTime();

  let foundKey: string | null = null;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const timestampPart = key.split('-').pop();
      const timestamp = parseInt(timestampPart ?? '', 10);

      if (!isNaN(timestamp) && timestamp <= endOfDayTimestamp) {
        foundKey = key;
        break;
      }
    }
  }

  // Clean up old ones
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayStartTimestamp = todayStart.getTime();

  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const timestampPart = key.split('-').pop();
      const timestamp = parseInt(timestampPart ?? '', 10);
      if (!isNaN(timestamp) && timestamp < todayStartTimestamp) {
        localStorage.removeItem(key);
      }
    }
  }

  return foundKey ? localStorage.getItem(foundKey) : null;
}
